import { useId, useMemo, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useIteroRx } from '../../../../hooks/useIteroBrowse';
import type { IteroFullRx } from '../../../../services/iteroBrowse';
import { browseErrorText } from './browseErrorText';
import { InlineError, SkeletonLines } from './BrowserPrimitives';
import { CopyButton } from './CopyButton';
import { DetailList } from './DetailList';
import { BROWSE_FORMATTERS } from './browseFormatters';
import { CARD_CLASS } from './browserStyles';
import { rxJson, rxSummaryRows } from './iteroBrowseFormat';
import { useIteroBrowserScope } from './iteroBrowserScope';

function RxContent({ rx }: { rx: IteroFullRx }) {
    const json = useMemo(() => rxJson(rx.raw), [rx.raw]);
    const rows = useMemo(() => rxSummaryRows(rx, BROWSE_FORMATTERS), [rx]);
    return (
        <div className="flex flex-col gap-3">
            <DetailList rows={rows} />
            <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-200">
                    <span className="text-[11px] font-semibold text-gray-500">Raw Rx JSON</span>
                    <CopyButton text={json} label="Rx JSON" caption="Copy JSON" />
                </div>
                <OverlayScrollArea contentClassName="max-h-[320px]" trackInsetPx={4}>
                    <pre
                        tabIndex={0}
                        aria-label="Raw Rx JSON"
                        className="px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-700 whitespace-pre-wrap break-all focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400"
                    >
                        {json}
                    </pre>
                </OverlayScrollArea>
            </div>
        </div>
    );
}

function RxBody({ rxId }: { rxId: string }) {
    const scope = useIteroBrowserScope();
    const rx = useIteroRx(scope, rxId, true);

    if (rx.isPending) return <SkeletonLines count={6} />;
    if (rx.isError) {
        return (
            <InlineError
                message={browseErrorText(rx.error, "Couldn't load the full Rx")}
                onRetry={() => rx.refetch()}
            />
        );
    }
    return <RxContent rx={rx.data} />;
}

// Closed by default and fetched on first open: the full Rx is a separate, audited iTero call.
export function FullRxSection({ rxId }: { rxId: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const panelId = useId();

    return (
        <section className={`${CARD_CLASS} overflow-hidden`}>
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="flex h-11 w-full items-center gap-2 px-3.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400"
            >
                <ChevronRightIcon
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    aria-hidden="true"
                />
                <span className="text-[13px] font-semibold text-gray-900">Full Rx</span>
                <span className="ml-auto text-[12px] text-gray-400">Clinical prescription</span>
            </button>
            <div id={panelId} hidden={!isOpen} className="border-t border-gray-100 px-3.5 py-3">
                {isOpen && <RxBody rxId={rxId} />}
            </div>
        </section>
    );
}
